import { Autocomplete } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, podatki, type Shema } from '../api/odjemalec';
import type { VrstaPostavke } from './delavnica';

export type PostavkaCenika = Shema['PostavkaCenika'];

/**
 * Naziv postavke z iskanjem po ceniku delavnice izbrane vrste: izbira iz cenika izpolni postavko ({@code izbrana}),
 * vpisati je mogoče tudi novo ime – taka postavka se ob shranjevanju doda v cenik (strežnik).
 */
export function IzbiraCenika({
  vrsta,
  naziv,
  nastaviNaziv,
  izbrana,
  napaka,
  oznaka = 'Naziv',
}: {
  vrsta: VrstaPostavke | undefined;
  naziv: string;
  nastaviNaziv: (n: string) => void;
  izbrana: (p: PostavkaCenika) => void;
  napaka?: string;
  oznaka?: string;
}) {
  const [iskanje] = useDebouncedValue(naziv, 250);
  const zadetki = useQuery({
    queryKey: ['cenik-delavnice', vrsta, iskanje],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/delavnica/cenik', { signal, params: { query: { vrsta, iskanje: iskanje || undefined } } })),
    placeholderData: keepPreviousData,
    enabled: !!vrsta,
  });
  const poNazivu = new Map((zadetki.data ?? []).map((p) => [p.naziv ?? '', p]));
  return (
    <Autocomplete
      label={oznaka}
      placeholder="Poiščite v ceniku ali vpišite novo"
      data={[...poNazivu.keys()].slice(0, 30)}
      value={naziv}
      // ime iz cenika (izbira ali natančno vpisano) izpolni postavko, sicer je to novo ime brez povezave s cenikom
      onChange={(v) => {
        const p = poNazivu.get(v);
        if (p) {
          izbrana(p);
        } else {
          nastaviNaziv(v);
        }
      }}
      onBlur={() => naziv !== naziv.trim() && nastaviNaziv(naziv.trim())}
      onOptionSubmit={(v) => {
        const p = poNazivu.get(v);
        if (p) {
          izbrana(p);
        }
      }}
      filter={({ options }) => options}
      error={napaka}
      style={{ flex: '1 1 220px' }}
    />
  );
}
